import React from 'react';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const Services = () => {
  const mainServices = [
    {
      title: 'Creator Backend Strategy',
      description: 'We help creators structure the business layer behind their content, including offers, funnels, conversion paths, lead handling, and monetization systems.'
    },
    {
      title: 'Offer Design & Positioning',
      description: 'We design and position offers that match the creator\'s audience, niche, expertise, and current stage.'
    },
    {
      title: 'Info Product Funnel Structure',
      description: 'We help build the path from content to lead to buyer through cleaner funnels, entry points, sales assets, and conversion logic.'
    },
    {
      title: 'Community & Mentorship Backend',
      description: 'We help structure low-ticket communities, paid groups, high-ticket mentorships, backend upsells, onboarding, retention, and buyer experience.'
    },
    {
      title: 'Brand & Market Positioning',
      description: 'We help sharpen how the creator or digital brand is presented, what they are known for, and why their audience should trust and buy from them.'
    },
    {
      title: 'Marketing & Campaign Support',
      description: 'We support content strategy, campaign planning, promotional direction, digital marketing execution, and performance-focused marketing systems.'
    }
  ];

  const capabilities = [
    {
      title: 'Marketing & Advertising',
      description: 'To carry on the business of a marketing and advertising agency and provide services relating to digital marketing, online marketing, and offline marketing.'
    },
    {
      title: 'Digital Marketing Channels',
      description: 'To undertake social media marketing, search engine optimization, search engine marketing, pay-per-click advertising, content creation, influencer marketing, email marketing, affiliate marketing, and performance marketing.'
    },
    {
      title: 'Branding & Brand Strategy',
      description: 'To provide branding, brand strategy, brand positioning, and brand management services including logo design, corporate identity development, and promotional campaigns.'
    },
    {
      title: 'Creative Production & Distribution',
      description: 'To conceptualize, design, develop, create, produce, publish, and distribute advertisements, creatives, audio-visual content, and promotional materials through print media, electronic media, digital platforms, and outdoor advertising.'
    },
    {
      title: 'Web, E-Commerce & Reputation',
      description: 'To provide website development, mobile application marketing, e-commerce marketing, and online reputation management services.'
    }
  ];

  return (
    <div style={{ background: 'var(--ink)', minHeight: '100vh' }}>
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ padding: '160px 0 80px' }}>
        <div
          className="glow"
          style={{
            width: 780, height: 780, left: '50%', top: -360,
            transform: 'translateX(-50%)',
            background: 'radial-gradient(circle, rgba(70,110,230,0.35), transparent 65%)',
          }}
        />
        <div className="relative max-w-[1160px] mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="show"
            variants={stagger}
            style={{ maxWidth: 820 }}
          >
            <motion.span variants={fadeUp} className="eyebrow">
              Services
            </motion.span>
            <motion.h1
              variants={fadeUp}
              style={{
                fontFamily: 'var(--display)',
                fontSize: 'clamp(38px, 5.6vw, 72px)',
                fontWeight: 900,
                letterSpacing: '-0.035em',
                lineHeight: 1.0,
                color: 'var(--text)',
              }}
            >
              Customer-acquisition systems.{' '}
              <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 400, color: 'var(--gold)' }}>
                Creator backends.
              </span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              style={{ fontSize: 19, color: 'var(--muted)', maxWidth: 640, marginTop: 24 }}
            >
              Solidcore Venture provides backend growth, offer strategy, conversion systems, brand positioning, and marketing infrastructure for businesses and creators.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section style={{ padding: '40px 0 100px' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 gap-4"
          >
            {mainServices.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="p-7 md:p-8 rounded-[18px] transition-colors"
                style={{
                  border: '1px solid var(--line-2)',
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(233,180,76,0.4)')}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--line-2)')}
              >
                <div className="flex items-start gap-4">
                  <span
                    className="font-mono flex-none"
                    style={{ fontSize: 12, color: 'var(--gold)', paddingTop: 4 }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3
                      style={{
                        fontFamily: 'var(--display)',
                        fontSize: 20,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        color: 'var(--text)',
                        marginBottom: 8,
                      }}
                    >
                      {service.title}
                    </h3>
                    <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.6 }}>
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Business Capabilities */}
      <section
        className="cream"
        style={{ background: 'var(--cream)', color: 'var(--char)', padding: '100px 0' }}
      >
        <div className="max-w-[1160px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
            style={{ maxWidth: 720 }}
          >
            <span className="eyebrow" style={{ color: '#A36F12' }}>
              Business capabilities
            </span>
            <h2
              style={{
                fontFamily: 'var(--display)',
                fontSize: 'clamp(30px, 4vw, 48px)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 1.0,
              }}
            >
              The full scope of{' '}
              <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: '#B07A16', fontWeight: 400 }}>
                what we do.
              </span>
            </h2>
            <p style={{ color: 'var(--char-2)', fontSize: 17, marginTop: 16 }}>
              Solidcore Venture operates across marketing, advertising, branding, content, campaign execution, and digital growth services.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-4"
          >
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="p-7 md:p-8 rounded-[16px]"
                style={{
                  background: 'var(--paper)',
                  border: '1px solid #E2D9C7',
                }}
              >
                <h3
                  style={{
                    fontFamily: 'var(--display)',
                    fontSize: 19,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: 'var(--char)',
                    marginBottom: 8,
                  }}
                >
                  {capability.title}
                </h3>
                <p style={{ color: 'var(--char-2)', fontSize: 15, lineHeight: 1.65 }}>
                  {capability.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden" style={{ padding: '120px 0' }}>
        <div
          className="glow"
          style={{
            width: 700, height: 700, left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(233,180,76,0.2), transparent 65%)',
          }}
        />
        <div className="relative max-w-[760px] mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{
              fontFamily: 'var(--display)',
              fontSize: 'clamp(34px, 4.8vw, 60px)',
              fontWeight: 900,
              letterSpacing: '-0.03em',
              lineHeight: 1.0,
              color: 'var(--text)',
            }}
          >
            Let's talk about{' '}
            <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--gold)', fontWeight: 400 }}>
              your acquisition.
            </span>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="mt-10"
          >
            <a href="/#contact" className="btn btn-gold btn-lg" data-testid="services-cta">
              Start a conversation <span className="arrow">→</span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
